import React, { useState, useMemo, useCallback } from 'react'
import {
    useGridApiContext,
    useGridSelector,
    gridColumnDefinitionsSelector,
    GridColDef,
    GridColumns,
} from '@mui/x-data-grid-pro'
import { useTranslation } from 'react-i18next'
import { PreferenceElState } from '../PartiTable'
import Menu from 'particles/Menu'
import MenuItem from 'particles/MenuItem'
import { EmptySearchResult } from 'particles/MenuItem/Search'
interface PreferencePanelProp {
    preferenceEl: PreferenceElState
    onClosePreferencePanel: () => void
    visibleModel: Record<string, boolean>
    setVisibleModel: (newModel: Record<string, boolean>) => void
}

const PreferencePanel: React.FC<PreferencePanelProp> = ({
    preferenceEl,
    onClosePreferencePanel,
    visibleModel,
    setVisibleModel,
}) => {
    const open = Boolean(preferenceEl.anchorEl)

    const [t] = useTranslation()
    const [searchValue, setSearchValue] = useState<string>('')
    const apiRef = useGridApiContext()
    const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector)
    const displayColumns = columns.filter((column: GridColDef) => column.field !== '__check__')

    const toggleColumn = (field: string) => {
        setVisibleModel({ ...visibleModel, [field]: !visibleModel[field] })
    }

    const toggleColumns: GridColumns = useMemo(() => {
        if (!searchValue) {
            return displayColumns
        } else {
            return displayColumns.filter((column: GridColDef) =>
                column.headerName?.toLowerCase()?.includes(searchValue),
            )
        }
    }, [searchValue, displayColumns])

    const handleSearchValueChange = useCallback((value: string) => {
        setSearchValue(value)
    }, [])

    const handleClearSearch = useCallback(() => {
        setSearchValue('')
    }, [])

    const toggleAllColumns = useCallback(
        (isVisible: boolean) => {
            const result: { [key: string]: boolean } = {}
            for (const key in visibleModel) {
                if (Object.prototype.hasOwnProperty.call(visibleModel, key)) {
                    result[key] = isVisible
                }
            }
            setVisibleModel(result)
        },
        [apiRef, visibleModel],
    )

    return (
        <Menu size="s" open={open} anchorEl={preferenceEl.anchorEl} onClose={onClosePreferencePanel} width={320}>
            <MenuItem.Search
                onSearch={handleSearchValueChange}
                onClearSearch={handleClearSearch}
                placeholder={t('placeholderText.enterColumnShowColumnMenu')}
                searchValue={searchValue}
            />
            {toggleColumns.length > 0 ? (
                toggleColumns.map((column: GridColDef, index) => {
                    return (
                        <MenuItem.Toggle
                            key={column.field}
                            tabIndex={index}
                            selected={visibleModel[column.field] !== false}
                            text={column.headerName ?? ''}
                            onClick={() => toggleColumn(column.field)}
                        />
                    )
                })
            ) : (
                <EmptySearchResult />
            )}
            <MenuItem.Divider />
            <MenuItem.Text text={t('button.hideAllColumns')} onClick={() => toggleAllColumns(false)} />
            <MenuItem.Text text={t('button.showAllColumns')} onClick={() => toggleAllColumns(true)} />
        </Menu>
    )
}

export default PreferencePanel
